import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "361",
  "slug": "countries-that-changed-their-names",
  "title": "The Countries That Changed Their Names (And Why)",
  "excerpt": "Türkiye, Eswatini, Czechia, North Macedonia, Myanmar — plenty of countries have changed what they call themselves. The reasons range from shedding colonial baggage to settling decades-long disputes.",
  "date": "2026-06-05",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1521295121783-8a321d551ad2?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx3b3JsZCUyMG1hcCUyMGdsb2JlfGVufDF8MHx8fDE3ODA2NDcxNDd8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "A country's name is rarely just a label. It carries history, identity, politics, and sometimes a grievance that has festered for decades. So when a nation decides to change what it calls itself, it is usually making a statement — about who it was, who it wants to be, or who it refuses to be confused with any longer. Here are some of the most notable name changes and the reasons behind them."
    },
    {
      "type": "heading",
      "text": "Shedding the Colonial Past"
    },
    {
      "type": "paragraph",
      "text": "Many name changes came with independence, as newly sovereign nations replaced the labels imposed by colonial rulers with names rooted in their own languages and history."
    },
    {
      "type": "list",
      "items": [
        "Sri Lanka was known as Ceylon under British rule; it took the name Sri Lanka when it became a republic in 1972, drawing on the island's older Sanskrit-derived name.",
        "Myanmar was renamed from Burma by the country's military government in 1989. The change remains politically loaded — some governments and opposition groups continued to use Burma — but Myanmar is the name now used by the United Nations.",
        "The Democratic Republic of the Congo has cycled through names including the Republic of the Congo and, under Mobutu, Zaire, before settling on its current name in the late 1990s."
      ]
    },
    {
      "type": "heading",
      "text": "Asserting a National Identity"
    },
    {
      "type": "paragraph",
      "text": "Some changes are less about colonialism and more about a country choosing the name it feels truly represents it — often a name it had always used at home."
    },
    {
      "type": "list",
      "items": [
        "Persia officially asked the world to call it Iran in 1935. Iran was the name Persians had long used for their own land; the change aligned the international name with the domestic one.",
        "Siam became Thailand in 1939, a shift tied to national identity and the idea of the land of the Thai people. The name briefly reverted before Thailand was readopted, and it has stuck since.",
        "Türkiye is the most recent high-profile example. In 2022 the country asked international bodies to use Türkiye — its own spelling — rather than the anglicized Turkey, partly to better reflect its language and partly to avoid the unflattering English associations of the older spelling."
      ]
    },
    {
      "type": "heading",
      "text": "Settling a Dispute"
    },
    {
      "type": "paragraph",
      "text": "Occasionally a name change is the price of peace, resolving a conflict that a single word had kept alive."
    },
    {
      "type": "paragraph",
      "text": "The clearest case is North Macedonia. For years, the country that emerged from the former Yugoslavia as simply 'Macedonia' was locked in a dispute with Greece, which has its own historic region of Macedonia and objected to the name. Under the 2019 Prespa Agreement, the country adopted the name North Macedonia, finally unblocking its path toward international organizations. A single qualifying word ended a decades-long standoff."
    },
    {
      "type": "heading",
      "text": "Clarity and Branding"
    },
    {
      "type": "paragraph",
      "text": "Not every change is dramatic; some are about being easier to find, file, and understand."
    },
    {
      "type": "list",
      "items": [
        "Czechia was formally adopted in 2016 as the short, geographic name for the Czech Republic — easier to put on a jersey or a map than the full political title, much as France is short for the French Republic.",
        "Eswatini replaced Swaziland in 2018, when the king announced a return to the country's precolonial name, partly to assert identity and partly to stop it being confused with Switzerland.",
        "Cabo Verde asked to be known by its Portuguese name rather than the translated Cape Verde, standardizing on a single form internationally."
      ]
    },
    {
      "type": "heading",
      "text": "The Netherlands and the Holland Mix-Up"
    },
    {
      "type": "paragraph",
      "text": "A subtler case is the Netherlands. Around 2020 the government leaned into a branding effort to consistently present the country as the Netherlands rather than Holland. Strictly speaking this was not a renaming — the country was always officially the Netherlands, and Holland refers only to two of its provinces — but it was a deliberate push to retire a long-standing nickname in official and promotional contexts. It is a useful reminder that a 'country name' and the casual name people use are not always the same thing."
    },
    {
      "type": "heading",
      "text": "Why This Matters for Geography"
    },
    {
      "type": "paragraph",
      "text": "Name changes are a small headache for map-makers and a quiet test of how current your knowledge is. They also reveal something deeper: borders may be drawn on the ground, but identity is carried in language. A country renaming itself is redrawing a border in people's minds rather than on the land."
    }
  ]
};
